import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

const PROJECT_TYPES = ["Късометражен", "Реклама", "Документален", "Музикален клип", "Моден филм"];
const API = process.env.REACT_APP_BACKEND_URL;

export const RequestModal = ({ open, onClose }) => {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", project_type: PROJECT_TYPES[0], message: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(false);
    try {
      const res = await fetch(`${API}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setSent(true);
    } catch {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    setSent(false);
    setError(false);
    setForm({ name: "", email: "", project_type: PROJECT_TYPES[0], message: "" });
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="request-modal"
          className="fixed inset-0 z-50 grid place-items-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <button aria-label="Затвори" onClick={close} className="absolute inset-0 bg-[#14110D]/60 backdrop-blur-sm" />
          <motion.div
            className="relative max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-[1.75rem] border border-[#14110D]/15 bg-[#F4F1EA] p-8"
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C8402A]">Заяви проект</p>
                <h3 className="mt-2 font-serif text-3xl text-[#14110D]">Alex Cholakov</h3>
              </div>
              <button
                data-testid="request-modal-close"
                aria-label="Затвори формата"
                onClick={close}
                className="grid h-10 w-10 place-items-center rounded-full border border-[#14110D]/20 transition-colors hover:bg-[#14110D] hover:text-[#F4F1EA]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {sent ? (
              <div data-testid="request-success" className="mt-10 text-center">
                <p className="font-serif text-3xl italic text-[#C8402A]">Благодарим!</p>
                <p className="mt-3 text-sm leading-relaxed text-[#524E48]">
                  Запитването е получено. Ще се свържем с вас в рамките на два работни дни.
                </p>
                <button
                  data-testid="request-success-close"
                  onClick={close}
                  className="mt-8 rounded-full bg-[#14110D] px-8 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-[#F4F1EA]"
                >
                  Затвори
                </button>
              </div>
            ) : (
              <form data-testid="request-form" onSubmit={submit} className="mt-8 space-y-5">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C867E]">Име / Организация</span>
                  <input
                    required
                    data-testid="request-input-name"
                    value={form.name}
                    onChange={set("name")}
                    className="mt-2 w-full rounded-xl border border-[#14110D]/20 bg-[#FAF8F5] px-4 py-3 text-sm text-[#14110D] outline-none transition-colors focus:border-[#C8402A]"
                    placeholder="Вашето име"
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C867E]">Имейл адрес</span>
                  <input
                    required
                    type="email"
                    data-testid="request-input-email"
                    value={form.email}
                    onChange={set("email")}
                    className="mt-2 w-full rounded-xl border border-[#14110D]/20 bg-[#FAF8F5] px-4 py-3 text-sm text-[#14110D] outline-none transition-colors focus:border-[#C8402A]"
                    placeholder="ime@primer.bg"
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C867E]">Тип проект</span>
                  <select
                    data-testid="request-input-type"
                    value={form.project_type}
                    onChange={set("project_type")}
                    className="mt-2 w-full rounded-xl border border-[#14110D]/20 bg-[#FAF8F5] px-4 py-3 text-sm text-[#14110D] outline-none transition-colors focus:border-[#C8402A]"
                  >
                    {PROJECT_TYPES.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C867E]">
                    Описание на концепцията
                  </span>
                  <textarea
                    rows={4}
                    data-testid="request-input-brief"
                    value={form.message}
                    onChange={set("message")}
                    className="mt-2 w-full resize-none rounded-xl border border-[#14110D]/20 bg-[#FAF8F5] px-4 py-3 text-sm text-[#14110D] outline-none transition-colors focus:border-[#C8402A]"
                    placeholder="Разкажете ни за идеята, локациите и сроковете…"
                  />
                </label>
                {error && (
                  <p data-testid="request-error" className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#C8402A]">
                    Нещо се обърка. Опитайте отново.
                  </p>
                )}
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="request-submit-button"
                  className="w-full rounded-full bg-[#C8402A] py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-[#F4F1EA] transition-colors duration-300 hover:bg-[#B0341F] disabled:opacity-60"
                >
                  {sending ? "Изпращане…" : "Изпрати запитване ↗"}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
